import { useState, useEffect } from 'react';
import { Search, Trash2, Droplet, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { AdminLayout } from '@/components/AdminLayout';
import { sizeApi } from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

export default function AdminSizes() {
    const [sizes, setSizes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingSize, setEditingSize] = useState(null);

    const [formData, setFormData] = useState({
        name: ''
    });

    useEffect(() => {
        loadSizes();
    }, []);

    const loadSizes = async () => {
        try {
            const res = await sizeApi.getAll(); // <-- backend endpoint for sizes
            setSizes(res.data);
        } catch (error) {
            toast.error('Failed to load sizes');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (size = null) => {
        if (size) {
            setEditingSize(size);
            setFormData({
                name: size.name,
            });
        } else {
            resetForm();
        }
        setDialogOpen(true);
    };

    const resetForm = () => {
        setFormData({ name: '' });
        setEditingSize(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            toast.error('Size name is required');
            return;
        }

        try {
            if (editingSize) {
                await sizeApi.update(editingSize.id, formData);
                toast.success('Size updated!');
            } else {
                await sizeApi.create(formData);
                toast.success('Size created!');
            }
            setDialogOpen(false);
            loadSizes();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save size');
        }
    };

    const handleDelete = async (sizeId) => {
        if (!window.confirm('Are you sure you want to delete this size?')) return;

        try {
            await sizeApi.delete(sizeId);
            toast.success('Size deleted');
            loadSizes();
        } catch (error) {
            toast.error('Failed to delete size');
        }
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const filteredSizes = sizes.filter((c) =>
        c.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <AdminLayout>
            <div className="space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-white">Sizes</h1>
                        <p className="text-stone-400 mt-1">{sizes.length} sizes</p>
                    </div>
                    <Button
                        onClick={() => handleOpenDialog()}
                        className="bg-emerald-600 hover:bg-emerald-700"
                        data-testid="add-size-btn"
                    >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Size
                    </Button>
                </div>

                {/* Search */}
                <div className="relative max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-500" />
                    <Input
                        placeholder="Search by size name..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 bg-stone-900 border-stone-700 text-white"
                        data-testid="size-search"
                    />
                </div>

                {/* Sizes Table */}
                <div className="bg-stone-900 border border-stone-800 overflow-x-auto">
                    <table className="w-full" data-testid="sizes-table">
                        <thead>
                            <tr className="border-b border-stone-800">
                                <th className="text-left p-4 text-stone-400 font-medium">Size</th>
                                <th className="text-left p-4 text-stone-400 font-medium">Created</th>
                                <th className="text-right p-4 text-stone-400 font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={4} className="text-center py-8 text-stone-500">Loading...</td>
                                </tr>
                            ) : filteredSizes.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="text-center py-8 text-stone-500">No sizes found</td>
                                </tr>
                            ) : (
                                filteredSizes.map((size) => (
                                    <tr
                                        key={size.id}
                                        className="border-b border-stone-800 hover:bg-stone-800/50"
                                        data-testid={`size-row-${size.id}`}
                                    >
                                        <td className="p-4 flex items-center gap-3">
                                            <div
                                                className="w-8 h-8 rounded-full border"
                                                style={{ backgroundSize: size.hex }}
                                            />
                                            <span className="font-medium text-white">{size.name}</span>
                                        </td>
                                        <td className="p-4 text-stone-400 text-sm">{formatDate(size.created_at)}</td>
                                        <td className="p-4">
                                            <div className="flex justify-end">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => handleDelete(size.id)}
                                                    className="text-stone-400 hover:text-red-400"
                                                    data-testid={`delete-size-${size.id}`}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent className="bg-stone-900 border-stone-800">
                        <DialogHeader>
                            <DialogTitle className="text-white">
                                {editingSize ? 'Edit Size' : 'Add New Size'}
                            </DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <Label className="text-stone-300">Size Name *</Label>
                                <Input
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="mt-1 bg-stone-800 border-stone-700 text-white"
                                    placeholder="e.g. Red"
                                    data-testid="size-name-input"
                                />
                            </div>

                            <DialogFooter>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setDialogOpen(false)}
                                    className="border-stone-700 text-stone-300"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    className="bg-emerald-600 hover:bg-emerald-700"
                                    data-testid="save-size-btn"
                                >
                                    {editingSize ? 'Update Size' : 'Add Size'}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
        </AdminLayout>
    );
}
